import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BASE_URL = process.env.REACT_APP_API_URL;

const BusinessAuditorUserProfile = () => {
  const [profileImage, setProfileImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [activeTab, setActiveTab] = useState("profileDetails");
  // Add state to track existing profile image path
  const [existingImagePath, setExistingImagePath] = useState(null);
  const [loading, setLoading] = useState(true);

  const [auditor, setAuditor] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    emailAddress: "",
    profileImage: "",
    CompanyCode: "",
    role: "",
    accountType: ""
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');

  const auditorData = JSON.parse(localStorage.getItem("auditor"));
  const auditorId = auditorData?.id;

  // Add toast notification helper
  const notify = (message, type = 'success') => toast[type](message, {
    position: "top-right",
    autoClose: 3000
  });

  useEffect(() => {
    const fetchAuditorData = async () => {
      if (!auditorId) {
        notify("Invalid auditor ID provided.", "error");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`${BASE_URL}/get-business-auditor/${auditorId}`);
        if (!response.ok) {
          throw new Error('Failed to fetch auditor data');
        }
        const data = await response.json();
        setAuditor(data);
        
        // Set preview image if exists and store the path for later deletion
        if (data.profileImage) {
          setPreviewImage(`${BASE_URL}${data.profileImage}`);
          setExistingImagePath(data.profileImage); // Store path for later deletion
        }
      } catch (error) {
        console.error("Error loading auditor data:", error);
        notify("Failed to load auditor data.", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchAuditorData();
  }, [auditorId]); // Add notify to dependencies if ESLint complains

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAuditor(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    if (!auditor.firstName || !auditor.lastName) {
      notify("First name and last name are required.", "error");
      return;
    }

    try {
      // If a new profile image is being uploaded and there's an existing one, delete the old one
      if (profileImage && existingImagePath) {
        try {
          // Call API to delete the old profile image
          const deleteResponse = await fetch(`${BASE_URL}/delete-auditor-profile-image`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              auditorId: auditorId,
              imagePath: existingImagePath
            })
          });
          
          if (!deleteResponse.ok) {
            console.warn('Failed to delete old profile image, but continuing with update');
          } else {
          }
        } catch (deleteError) {
          console.warn('Error deleting old profile image:', deleteError);
          // Don't block the update if deletion fails
        }
      }

      const formData = new FormData();
      formData.append("firstName", auditor.firstName);
      formData.append("lastName", auditor.lastName);
      formData.append("phoneNumber", auditor.phoneNumber);
      formData.append("country", auditor.country);
      
      if (profileImage) {
        formData.append("profileImage", profileImage);
      }

      const response = await fetch(`${BASE_URL}/update-business-auditor/${auditorId}`, {
        method: 'PUT',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      const data = await response.json();
      
      // Update the auditor state with new data including image
      setAuditor(prev => ({
        ...prev,
        firstName: data.firstName,
        lastName: data.lastName,
        phoneNumber: data.phoneNumber,
        profileImage: data.profileImage // Update the image path
      }));

      // Update preview image with full URL and store the new path
      if (data.profileImage) {
        setPreviewImage(`${BASE_URL}${data.profileImage}`);
        setExistingImagePath(data.profileImage); // Update existing path for future changes
      }

      // Update localStorage with full data
      const updatedAuditorData = {
        ...auditorData,
        ...data
      };
      localStorage.setItem('auditor', JSON.stringify(updatedAuditorData));

      // Reset the file input
      setProfileImage(null);
      document.getElementById('profileImage').value = '';

      notify("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating profile:", error);
      notify("Error updating profile. Please try again.", "error");
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    setPasswordError('');
  };

  const handlePasswordUpdate = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordData;

    if (!currentPassword || !newPassword || !confirmPassword) {
      notify('All fields are required', "error");
      return;
    }

    if (newPassword !== confirmPassword) {
      notify('New passwords do not match', "error");
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/update-business-auditor/${auditorId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          password: newPassword
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update password');
      }

      notify('Password updated successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordError('');
    } catch (error) {
      notify('Failed to update password', "error");
    }
  };

  return (
    <div className="container-fluid" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <ToastContainer />
      <div className="row justify-content-center py-5">
        <div className="col-12 col-md-10">
          <div className="card shadow-sm">
            <div className="card-header bg-white py-3">
              <h4 className="mb-0 text-primary">My Profile</h4>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-primary fw-bold mb-1">Loading user profile</p>
                  <p className="text-muted">Please wait while we fetch user profile data...</p>
                </div>
              ) : (
                <div className="row">
                  {/* Profile Image Section */}
                  <div className="col-md-3 text-center mb-4">
                    <div className="position-relative d-inline-block">
                      <img
                        src={
                          previewImage || 
                          (auditor.profileImage ? `${BASE_URL}${auditor.profileImage}` : "https://www.w3schools.com/w3images/avatar2.png")
                        }
                        alt="Profile"
                        className="rounded-circle mb-3"
                        style={{
                          width: "150px",
                          height: "150px",
                          objectFit: "cover",
                          border: "3px solid #007bff"
                        }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "https://www.w3schools.com/w3images/avatar2.png";
                        }}
                      />
                      <label
                        htmlFor="profileImage"
                        className="btn btn-sm btn-primary position-absolute bottom-0 end-0"
                        style={{ borderRadius: "50%", padding: "8px" }}
                      >
                        <i className="fas fa-camera"></i>
                      </label>
                      <input
                        type="file"
                        id="profileImage"
                        className="d-none"
                        onChange={handleImageChange}
                        accept="image/*"
                      />
                    </div>
                    <h5 className="mt-3">{auditor.firstName} {auditor.lastName}</h5>
                  </div>

                  {/* Profile Details Section */}
                  <div className="col-md-9">
                    <ul className="nav nav-tabs mb-4">
                      <li className="nav-item">
                        <button
                          className={`nav-link ${activeTab === "profileDetails" ? "active" : ""}`}
                          onClick={() => setActiveTab("profileDetails")}
                        >
                          Profile Details
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          className={`nav-link ${activeTab === "changePassword" ? "active" : ""}`}
                          onClick={() => setActiveTab("changePassword")}
                        >
                          Security
                        </button>
                      </li>
                    </ul>

                    {activeTab === "profileDetails" && (
                      <form>
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label className="form-label">First Name</label>
                            <input
                              type="text"
                              name="firstName"
                              className="form-control"
                              value={auditor.firstName}
                              onChange={handleInputChange}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Last Name</label>
                            <input
                              type="text"
                              name="lastName"
                              className="form-control"
                              value={auditor.lastName}
                              onChange={handleInputChange}
                            />
                          </div>
                        </div>

                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label className="form-label">Email Address</label>
                            <input
                              type="email"
                              className="form-control"
                              value={auditor.emailAddress}
                              disabled
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Phone Number</label>
                            <input
                              type="text"
                              name="phoneNumber"
                              className="form-control"
                              value={auditor.phoneNumber}
                              onChange={handleInputChange}
                            />
                          </div>
                        </div>

                        <div className="d-flex justify-content-end">
                          <button
                            type="button"
                            className="btn btn-primary px-4"
                            onClick={handleSave}
                          >
                            Save Changes
                          </button>
                        </div>
                      </form>
                    )}

                    {activeTab === "changePassword" && (
                      <form>
                        {passwordError && (
                          <div className="alert alert-danger">{passwordError}</div>
                        )}
                        <div className="row mb-3">
                          <div className="col-md-12">
                            <label className="form-label">Current Password</label>
                            <input
                              type="password"
                              name="currentPassword"
                              className="form-control"
                              value={passwordData.currentPassword}
                              onChange={handlePasswordChange}
                            />
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label className="form-label">New Password</label>
                            <input
                              type="password"
                              name="newPassword"
                              className="form-control"
                              value={passwordData.newPassword}
                              onChange={handlePasswordChange}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Confirm New Password</label>
                            <input
                              type="password"
                              name="confirmPassword"
                              className="form-control"
                              value={passwordData.confirmPassword}
                              onChange={handlePasswordChange}
                            />
                          </div>
                        </div>
                        <div className="d-flex justify-content-end">
                          <button
                            type="button"
                            className="btn btn-primary px-4"
                            onClick={handlePasswordUpdate}
                          >
                            Update Password
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessAuditorUserProfile;